// Every viewer-specific string of the Connect finance module lives here so
// the tables and cards stay presentational. The restaurant copy never names
// Stripe's buy rate; the admin copy is allowed to.
const sections = {
  // Neither a table nor a title of its own — the strip renders its own
  // labels (COPY.balance). Only the error string is read, by the container's
  // generic per-section catch, when the balance read itself fails.
  balance: {
    title: '',
    description: '',
    empty: '',
    error: "Couldn't load your balance.",
  },
  // Overview's two previews. Only their error lines are read from here — the
  // headings and empty states live on the page, because the empty state names
  // the window the page chose.
  payoutActivity: {
    title: 'Payout activity',
    description: '',
    empty: '',
    error: "Couldn't load your recent payouts.",
  },
  recentOrders: {
    title: 'Recent orders',
    description: '',
    empty: '',
    error: "Couldn't load your recent orders.",
  },
  // The summary has no table of its own; only its error line is read.
  summary: {
    title: 'Summary',
    description: '',
    empty: '',
    error: "Couldn't load the summary.",
  },
  // Period-scoped (READERS.payments takes {from, to}): the picker sitting
  // above this table is the actual fix for an empty result, so the empty
  // copy names it instead of just restating that the period is why.
  payments: {
    title: 'Payments',
    description: 'Charges that settled on the connected Stripe account in this period.',
    empty: 'No payments in this period. Try a different period above.',
    error: "Couldn't load payments.",
  },
  // Not period-scoped — the API has no date filter for refunds (READERS.refunds
  // ignores {from, to}), so this must never claim the picker would change
  // anything, unlike its four siblings below.
  refunds: {
    title: 'Refunds',
    description: 'Every refund issued on the connected account, newest first.',
    empty: 'No refunds yet.',
    error: "Couldn't load refunds.",
  },
  // Period-scoped for the admin's own table (READERS.disputes takes
  // {from, to}); the restaurant never reads this copy at all, since its
  // Disputes tab is Stripe's own embedded component instead.
  disputes: {
    title: 'Disputes',
    description: 'Chargebacks raised against payments on the connected account.',
    empty: 'No disputes in this period. Try a different period above.',
    error: "Couldn't load disputes.",
  },
  // Period-scoped for the admin's own table (READERS.payouts takes
  // {from, to}); same split as disputes — the restaurant's Payouts tab is
  // Stripe's own embedded component and never reads this copy.
  payouts: {
    title: 'Payouts',
    description: 'Transfers from the connected Stripe account to the bank on file.',
    empty: 'No payouts in this period. Try a different period above.',
    error: "Couldn't load payouts.",
  },
  // Period-scoped (READERS.applicationFees takes {from, to}); admin-only tab.
  applicationFees: {
    title: 'Application fees',
    description: "Thisway's collected fees on this account, one row per charge.",
    empty: 'No application fees in this period. Try a different period above.',
    error: "Couldn't load application fees.",
  },
  // Period-scoped (Connect/Finance/OrderReports takes companyid/from/to, plus
  // an optional storeid) — restaurant-only page, the order-by-order statement.
  // Called Transactions since 2026-10-06 (owner: "Order Reports" was
  // mistaken for the Reports entry in the same sidebar). The route, the
  // API and this key keep the old name.
  orderReports: {
    title: 'Transactions',
    // No tabs any more: refunds are ordinary rows of the one statement, and
    // the description covers both what was paid and what went back.
    description: 'What you were paid, and what went back.',
    empty: 'No paid orders in this period. Try a different period above.',
    error: "Couldn't load transactions.",
  },
};

// The admin's own tab strip's accessible name (connect-finance.js — the
// restaurant has no tab strip any more, each of its sections is its own page
// under the Finance sidebar entry instead). Kept as its own constant rather
// than inlined so a second tabbed viewer, if one is ever added, doesn't have
// to hunt for this copy.
const tabsAriaLabel = 'Finance sections';

// The summary's two reconciling rows, identical for both viewers (neither
// names Stripe or a buy-rate figure — both are commercial numbers a
// restaurant can already work out from the gross, net and fees on screen).
//
// `feesNotCollected` is a credit, not a debt: on an order too small to carry
// its own fees, Thisway absorbs the shortfall rather than taking it from the
// restaurant, so the line must read as good news, plainly, with nothing that
// sounds like it could be clawed back later.
//
// `otherDeductions` is named for what it is to a restaurant reading the
// list, not for the API field it came from.
const summaryRows = {
  feesNotCollectedLabel: 'Fees waived',
  feesNotCollectedHint: 'Orders too small to cover their fees',
  otherDeductionsLabel: 'Other fees',
  otherDeductionsHint: 'Deducted beyond the fees above',
};

export const COPY = {
  summaryRows,
  balance: {
    available: 'Available balance',
    pending: 'Pending balance',
    nextPayout: 'Next payout',
    retryAriaLabel: 'Retry balance',
    // Shown by an embedded panel's own ErrorBoundary when a mounted Stripe
    // component throws after it has already rendered — never seen for a
    // session-mint failure, which the panel's own error state (above) covers
    // instead.
    widgetFailed: 'This section could not load. Refresh the page to try again.',
    // Stripe's own words for the schedule, in the restaurant's. Owner,
    // 2026-10-22: the restaurant's row names the interval only ("Daily
    // payouts") and the delay moved into the editor's daily line
    // (restaurant.scheduleDailyHelp); `dailyWithDelay` is the admin support
    // block's sentence, where support needs the delay.
    // Owner, 2026-10-06: a bare "Weekly" said too little. Each line says
    // what the restaurant can count on — which day, or that nothing leaves
    // by itself — and falls back to the interval alone when Stripe did not
    // send the day.
    schedule: {
      daily: 'Daily payouts',
      dailyWithDelay: (days) =>
        typeof days === 'number'
          ? `Daily payouts · arrive in ${days} business ${days === 1 ? 'day' : 'days'}`
          : 'Daily payouts',
      weekly: (weekday) => (weekday ? `Weekly · every ${weekday}` : 'Weekly payouts'),
      monthly: (dayOfMonth) => (dayOfMonth ? `Monthly · on the ${dayOfMonth}` : 'Monthly payouts'),
      // Not "Manual payouts" alone: on a manual schedule the Next payout
      // slot above this row already says exactly that.
      manual: 'Manual · paid out on request',
      unknown: 'Not scheduled yet',
    },
  },
  restaurant: {
    // Shown in place of the balance strip and both live panels when the
    // company has no Stripe account yet, or one still mid-onboarding —
    // mounting a Stripe component against an unfinished account shows an
    // empty frame with no explanation, so the container checks readiness
    // first and renders this sentence instead. Split around the link so the
    // container can render "Finance → Business Details" as a Link rather
    // than text.
    accountNotReady: {
      before: 'Finish setting up payments in ',
      linkLabel: 'Finance → Business Details',
      after: ' to see your balance, payouts and disputes.',
    },
    retry: 'Retry',

    // Round 2 (spec 2026-09-22 §10). Every sentence the new modals, the
    // payout detail page and the Business Details banner say. Interpolated
    // values are function parameters so no caller ever builds a sentence.
    // The restaurant's own schedule editor is back (owner, 2026-10-20, spec
    // 2026-10-20-payout-schedule-everywhere): daily payouts cost nothing, so
    // there is no reason to hold the interval any more. These are the
    // modal's sentences; the schedule's own wording lives in
    // balance.schedule above and is shared with the Manager app.
    payoutScheduleTitle: 'Payout schedule',
    payoutScheduleSub: 'Choose how often your money is sent to your bank.',
    scheduleManualHelp: 'You send each payout yourself.',
    // The line under the daily choice (owner, 2026-10-22). Without a delay
    // from Stripe there is nothing true to say about arrival, so no line.
    scheduleDailyHelp: (days) =>
      typeof days === 'number'
        ? `Sent every business day · arrives in ${days} business ${days === 1 ? 'day' : 'days'}.`
        : null,
    scheduleWeekdayPlaceholder: 'Choose a day',
    payoutScheduleSaved: 'Payout schedule saved',
    payoutScheduleFailed: "Couldn't save your payout schedule.",
    nothingToPayOutTitle: 'Nothing to pay out yet',
    nothingToPayOutPending: (pending, days) =>
      `${pending} pending becomes available in ${days} business days.`,
    nothingToPayOutEmpty: 'Payments you take will show here once they settle.',
    // Added on review of Task 5 (2026-09-22): StripeBalancesModal used to
    // render the Account Session hook's own error text verbatim, and every
    // real value that hook throws names the vendor ("This Stripe session
    // could not be started."). A restaurant never reads that name, so the
    // modal shows this fixed sentence instead, whatever the hook actually
    // said, and Try again still calls retrySession underneath it.
    sessionFailed: "We couldn't reach your payout account just now.",
    // "Thisway" is bolded on screen; the component splits this one string on
    // that word rather than holding two halves of a sentence.
    statementHelp1:
      'Banks can take up to 5 business days to post a deposit. Look for Thisway on your statement.',
    statementHelp2: (date) =>
      `If it hasn't appeared by ${date}, give your bank the trace ID below.`,
    payoutMissing: "This payout isn't in your account.",
    // A payout sent by hand — standard on request, or instant — is one
    // Stripe will not itemize (owner, 2026-09-23). Split around the link so
    // the page renders "Transactions" as a Link rather than text.
    payoutNotItemized: {
      before: "Stripe doesn't itemize payouts you send yourself, standard or instant. The orders they cover are in ",
      linkLabel: 'Transactions',
      after: '.',
    },
    // The Upcoming detail page's own header line (spec 2026-09-22 §13),
    // shown in place of "Sent …" / "Created …" — money not yet swept into
    // any Stripe payout has neither date.
    upcomingNotSentYet: 'Not sent yet — goes out with your next payout',
    // One Upcoming group's page (owner, 2026-09-23): it knows its date.
    upcomingArrivalExpected: (date) => `Not sent yet — expected to arrive ${date}`,
    // A shortfall's page (owner, 2026-09-23): nothing arrives, Stripe takes it.
    upcomingShortfall: 'Nothing to send — taken from your next sales, or from your bank account if there are none',
    exportFailed: "Couldn't export. Try again.",
    actionRequiredTitle: 'Action required',
    // The deadline sentence is appended by the banner when Stripe gave one,
    // so the same body serves both the amber and the red state.
    actionRequiredBody: 'Additional information is needed to keep your account active.',
    sections,
  },
  admin: {
    title: 'Settlement',
    intro:
      "Direct-charge activity on this company's Stripe account. Buy-rate figures are visible to admins only.",
    emptyPeriod: 'No Connect activity in this period.',
    disputeNote:
      "Evidence for disputes on the restaurant's Stripe account can't be submitted from the dashboard yet.",
    retry: 'Retry',
    refresh: 'Refresh',
    tabsAriaLabel,
    sections,
    // Payments table only, admin viewer only: the control that reveals
    // Thisway's application fee, Stripe's buy rate, the card-fee margin, the
    // reconciliation delta and the international flag — five columns kept
    // off by default so the table's other eight stay readable on a laptop.
    reconciliationToggle: {
      label: 'Show reconciliation detail',
      ariaLabel:
        'Show application fee, Stripe fee, margin, delta and international flag columns',
    },
  },
};

export default COPY;
